// src/adminDashboard/shared/components/DateRangePicker.tsx
// Reusable date range picker with presets

import { Calendar, ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

export interface DateRange {
    start: string;
    end: string;
}

interface Preset {
    label: string;
    value: string;
    days: number | null;
}

const PRESETS: Preset[] = [
    { label: 'Today', value: 'today', days: 0 },
    { label: 'Last 7 days', value: '7days', days: 7 },
    { label: 'Last 30 days', value: '30days', days: 30 },
    { label: 'Last 90 days', value: '90days', days: 90 },
    { label: 'Custom', value: 'custom', days: null },
];

interface DateRangePickerProps {
    value?: DateRange;
    onChange: (range: DateRange) => void;
    className?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState('30days');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    const toDateString = (date: Date) => date.toISOString().split('T')[0] ?? '';

    const getDateRange = (preset: string): DateRange => {
        const end = new Date();
        const start = new Date();

        if (preset === 'today') {
            start.setHours(0, 0, 0, 0);
        } else if (preset !== 'custom') {
            const days = PRESETS.find(p => p.value === preset)?.days || 30;
            start.setDate(end.getDate() - days);
        }

        return {
            start: toDateString(start),
            end: toDateString(end),
        };
    };

    const handlePresetClick = (preset: string) => {
        setSelectedPreset(preset);

        if (preset !== 'custom') {
            const range = getDateRange(preset);
            onChange(range);
            setIsOpen(false);
        }
    };

    const handleCustomApply = () => {
        if (customStart && customEnd) {
            onChange({ start: customStart, end: customEnd });
            setIsOpen(false);
        }
    };

    const getDisplayText = () => {
        if (!value) return 'Select date range';

        const preset = PRESETS.find(p => p.value === selectedPreset);
        if (selectedPreset !== 'custom') {
            return preset?.label || 'Last 30 days';
        }

        return `${value.start} - ${value.end}`;
    };

    return (
        <div className={`relative ${className}`}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition-colors"
            >
                <Calendar size={16} className="text-slate-400" />
                <span className="text-slate-700">{getDisplayText()}</span>
                <ChevronDown size={16} className="text-slate-400" />
            </button>

            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Dropdown */}
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 z-50">
                        <div className="p-4">
                            {/* Presets */}
                            <div className="space-y-1 mb-4">
                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                                    Quick Select
                                </p>
                                {PRESETS.map((preset) => (
                                    <button
                                        key={preset.value}
                                        onClick={() => handlePresetClick(preset.value)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedPreset === preset.value
                                            ? 'bg-primary/10 text-primary font-medium'
                                            : 'hover:bg-slate-50 text-slate-700'
                                            }`}
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>

                            {/* Custom Date Inputs */}
                            {selectedPreset === 'custom' && (
                                <div className="pt-4 border-t border-slate-200">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                                        Custom Range
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs text-slate-600 mb-1">
                                                Start Date
                                            </label>
                                            <input
                                                type="date"
                                                value={customStart}
                                                onChange={(e) => setCustomStart(e.target.value)}
                                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-600 mb-1">
                                                End Date
                                            </label>
                                            <input
                                                type="date"
                                                value={customEnd}
                                                onChange={(e) => setCustomEnd(e.target.value)}
                                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            />
                                        </div>
                                        <button
                                            onClick={handleCustomApply}
                                            disabled={!customStart || !customEnd}
                                            className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default DateRangePicker;
